import { createFileRoute, Link, notFound } from "@tanstack/react-router";
import { ArrowRight, CheckCircle2, MapPin, Sparkles, Clock, ShieldCheck, Wrench } from "lucide-react";
import { INDUSTRIES, INDUSTRY_LIST, type IndustrySlug } from "@/data/industries";
import { INDUSTRY_SEO } from "@/data/industry-seo";
import { STATES_DATA } from "@/data/states";
import { ogImageMeta } from "@/lib/og-image";
import { trackApplyClick } from "@/lib/apply-cta";

const INDUSTRY_SLUGS = new Set<string>(INDUSTRY_LIST.map((i) => i.slug));
const STATE_SLUGS = new Set(STATES_DATA.map((s) => s.slug));

export const Route = createFileRoute("/industries/$industry/$state")({
  beforeLoad: ({ params }) => {
    if (!INDUSTRY_SLUGS.has(params.industry) || !STATE_SLUGS.has(params.state)) {
      throw notFound();
    }
  },
  loader: ({ params }) => {
    const industry = INDUSTRIES[params.industry as IndustrySlug];
    const state = STATES_DATA.find((s) => s.slug === params.state)!;
    return { industry, state };
  },
  head: ({ params, loaderData }) => {
    const ind = loaderData?.industry ?? INDUSTRIES[params.industry as IndustrySlug];
    const st = loaderData?.state ?? STATES_DATA.find((s) => s.slug === params.state)!;
    const seo = INDUSTRY_SEO[params.industry as IndustrySlug];
    const url = `https://vendingmachinefree.com/industries/${params.industry}/${params.state}`;
    const title = `${ind.short} Vending Machine Placement in ${st.name} | Free Installation & Service`;
    const description = `Free vending machines for ${ind.name.toLowerCase()} in ${st.name}. ${seo?.placementPhrase ?? "Industry vending placement"} with installation, restocking, and 24/7 maintenance — zero cost. Fast approval, 4–6 hour response.`;
    return {
      meta: [
        { title },
        { name: "description", content: description },
        { name: "keywords", content: [seo?.placementPhrase, `${ind.short.toLowerCase()} vending ${st.name.toLowerCase()}`, `free vending machines ${st.name.toLowerCase()}`, ...(seo?.keywords ?? [])].filter(Boolean).join(", ") },
        { property: "og:title", content: title },
        { property: "og:description", content: description },
        { property: "og:url", content: url },
        { property: "og:type", content: "website" },
        { name: "twitter:title", content: title },
        { name: "twitter:description", content: description },
        ...ogImageMeta(),
      ],
      links: [{ rel: "canonical", href: url }],
      scripts: [
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@graph": [
              {
                "@type": "LocalBusiness",
                name: `Vending Machine Free.com — ${ind.short} Vending in ${st.name}`,
                url,
                image: "https://vendingmachinefree.com/og-image.jpg",
                areaServed: { "@type": "State", name: st.name },
                description: `${seo?.placementPhrase ?? "Free vending machine placement"} for ${ind.name.toLowerCase()} across ${st.name}.`,
              },
              {
                "@type": "Service",
                serviceType: `${seo?.placementPhrase ?? "Vending Machine Placement"} — ${st.name}`,
                provider: { "@type": "Organization", name: "Vending Machine Free.com", url: "https://vendingmachinefree.com" },
                areaServed: { "@type": "State", name: st.name },
                offers: { "@type": "Offer", price: "0", priceCurrency: "USD" },
              },
              {
                "@type": "FAQPage",
                mainEntity: buildLocalFaqs(ind.short, st.name).map((f) => ({
                  "@type": "Question",
                  name: f.q,
                  acceptedAnswer: { "@type": "Answer", text: f.a },
                })),
              },
              {
                "@type": "BreadcrumbList",
                itemListElement: [
                  { "@type": "ListItem", position: 1, name: "Home", item: "https://vendingmachinefree.com/" },
                  { "@type": "ListItem", position: 2, name: "Industries", item: "https://vendingmachinefree.com/industries" },
                  { "@type": "ListItem", position: 3, name: ind.name, item: `https://vendingmachinefree.com/industries/${params.industry}` },
                  { "@type": "ListItem", position: 4, name: st.name, item: url },
                ],
              },
            ],
          }),
        },
      ],
    };
  },
  component: IndustryStatePage,
  notFoundComponent: () => (
    <div className="min-h-screen flex items-center justify-center">
      <p>Page not found. <Link to="/" className="text-safety underline">Go home</Link>.</p>
    </div>
  ),
  errorComponent: () => (
    <div className="min-h-screen flex items-center justify-center">
      <p>Something went wrong. <Link to="/" className="text-safety underline">Go home</Link>.</p>
    </div>
  ),
});

function buildLocalFaqs(short: string, stateName: string) {
  return [
    { q: `How much does a vending machine cost for ${short.toLowerCase()} in ${stateName}?`, a: `Nothing. Vending Machine Free.com provides, installs, restocks, and maintains vending machines for ${short.toLowerCase()} across ${stateName} at zero cost. There's no lease, no purchase, and no hidden fees — we earn revenue from product sales.` },
    { q: `How fast can you install a vending machine at a ${short.toLowerCase()} in ${stateName}?`, a: `Most ${stateName} locations are approved within 48 hours and installed within 1–2 weeks. Our local ${stateName} route drivers handle delivery, setup, and initial stocking on the same visit.` },
    { q: `Do you service ${short.toLowerCase()} outside major ${stateName} cities?`, a: `Yes. We cover the entire state of ${stateName} — every major metro plus secondary and rural markets. If your ${short.toLowerCase()} is in ${stateName}, we can place a machine there.` },
    { q: `What products go into a ${short.toLowerCase()} vending machine in ${stateName}?`, a: `The mix is tailored to your ${short.toLowerCase()} — national-brand snacks and drinks, healthy options, energy drinks, and (for larger sites) fresh coffee or micro-market setups. Product selections are optimized monthly based on ${stateName} sales data.` },
    { q: `How quickly do you respond to service calls in ${stateName}?`, a: `Our ${stateName} service team targets a 4–6 hour response for restocks and 24–48 hours for repairs, backed by remote monitoring that flags empties and faults automatically.` },
    { q: `Can our ${short.toLowerCase()} earn commission on vending sales in ${stateName}?`, a: `Larger ${stateName} locations — typically 100+ daily users — qualify for an optional commission share on machine sales. We'll evaluate eligibility during your free site survey.` },
  ];
}

function IndustryStatePage() {
  const { industry: ind, state: st } = Route.useLoaderData();
  const seo = INDUSTRY_SEO[ind.slug as IndustrySlug];
  const localFaqs = buildLocalFaqs(ind.short, st.name);
  const topCityLabels = st.cities.slice(0, 6);

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="border-b border-border bg-background sticky top-0 z-40">
        <div className="container-x flex h-16 items-center justify-between">
          <Link to="/" className="font-display text-xl uppercase">
            Vending Machine Free<span className="text-safety">.com</span>
          </Link>
          <nav className="hidden md:flex items-center gap-6 text-sm">
            <Link to="/coverage" className="hover:text-safety">Coverage</Link>
            <Link to="/industries/$industry" params={{ industry: ind.slug }} className="hover:text-safety">
              {ind.short}
            </Link>
            <Link to="/states/$state" params={{ state: st.slug }} className="hover:text-safety">
              {st.name}
            </Link>
            <Link to="/contact" className="hover:text-safety">Contact</Link>
          </nav>
          <Link
            to="/"
            hash="contact"
            onClick={() => trackApplyClick(`industry-state:${ind.slug}:${st.slug}:header`)}
            className="inline-flex items-center gap-2 rounded-md bg-safety px-4 py-2 text-sm font-semibold text-safety-foreground"
          >
            Apply Free <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
      </header>

      {/* Hero */}
      <section className="py-16 md:py-24 bg-charcoal text-charcoal-foreground">
        <div className="container-x max-w-4xl">
          <div className="text-xs uppercase tracking-[0.2em] text-safety font-bold">
            {ind.name} · {st.name} · USA
          </div>
          <h1 className="mt-4 font-display text-4xl md:text-5xl lg:text-6xl uppercase">
            Free Vending Machine Placement for {ind.name} in {st.name}
          </h1>
          <p className="mt-6 text-lg text-white/85">
            {seo?.placementPhrase ? `${seo.placementPhrase.replace(/\b\w/g, (c: string) => c.toUpperCase())} — ` : ""}
            installed, restocked, and serviced across {st.name} at zero cost. Fast 48-hour approval, 1–2 week install, 4–6 hour response times.
          </p>
          <div className="mt-8 flex flex-wrap gap-3">
            <Link
              to="/"
              hash="contact"
              onClick={() => trackApplyClick(`industry-state:${ind.slug}:${st.slug}:hero`)}
              className="inline-flex items-center gap-2 rounded-md bg-safety px-6 py-3 font-semibold text-safety-foreground shadow-[var(--shadow-cta)]"
            >
              Check Eligibility in 60 Seconds <ArrowRight className="h-4 w-4" />
            </Link>
            <Link to="/contact" className="inline-flex items-center gap-2 rounded-md border border-white/30 px-6 py-3 font-semibold text-white hover:bg-white/10">
              Speak to a {st.name} Specialist
            </Link>
          </div>
        </div>
      </section>

      {/* Triple Zero */}
      <section className="py-12 bg-secondary border-y border-border">
        <div className="container-x grid md:grid-cols-3 gap-6 text-center">
          {[
            { icon: ShieldCheck, t: "$0 Machine", d: `Zero-cost vending equipment placed at your ${st.name} ${ind.short.toLowerCase()}.` },
            { icon: Wrench, t: "$0 Delivery & Setup", d: `Free ${st.name}-based delivery, install, and initial stocking.` },
            { icon: Clock, t: "$0 Maintenance", d: `24/7 monitoring with 4–6 hour restocks and 24–48 hour repairs statewide.` },
          ].map((z) => (
            <div key={z.t} className="rounded-lg bg-card border border-border p-6">
              <z.icon className="h-8 w-8 text-safety mx-auto" />
              <h3 className="mt-2 font-display uppercase">{z.t}</h3>
              <p className="mt-1 text-sm text-muted-foreground">{z.d}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Intro / Long-form */}
      <section className="py-16 md:py-20">
        <div className="container-x max-w-3xl space-y-5 text-base leading-relaxed">
          <p className="text-lg text-foreground">
            Vending Machine Free.com serves {ind.name.toLowerCase()} across every county in {st.name}. Whether you operate a single facility in {topCityLabels[0] ?? st.name} or manage multiple sites throughout the state, our {seo?.placementPhrase ?? "vending placement"} program installs modern, cashless machines at zero cost and keeps them fully stocked and serviced by a locally based {st.name} route team.
          </p>
          <p className="text-muted-foreground">
            {ind.intro}
          </p>
          <p className="text-muted-foreground">
            In {st.name} specifically, we've optimized our program for the industries, workforce patterns, and product preferences that dominate the local market. {ind.deepDive[0]}
          </p>
          <p className="text-muted-foreground">
            {ind.deepDive[1] ?? ""}
          </p>
          <p className="text-muted-foreground">
            Every {st.name} placement is backed by remote-monitored inventory, tap-to-pay hardware (Apple Pay, Google Pay, chip cards), and a service SLA that keeps machines full and functional. Our {st.name} route density lets us hit 4–6 hour restocking windows in major metros and 24–48 hours in secondary markets — meaning your team, staff, or customers rarely see an empty selection.
          </p>
          {seo?.contentAngle && (
            <div className="rounded-lg border-l-4 border-safety bg-secondary p-5">
              <div className="flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-safety font-bold">
                <Sparkles className="h-4 w-4" /> Why It Works in {st.name}
              </div>
              <p className="mt-2 text-foreground">{seo.contentAngle}</p>
            </div>
          )}
        </div>
      </section>

      {/* Benefits */}
      <section className="py-16 bg-secondary">
        <div className="container-x">
          <h2 className="font-display text-3xl uppercase text-center">
            Why {ind.name} in {st.name} Choose Free Vending Placement
          </h2>
          <div className="mt-10 grid md:grid-cols-2 lg:grid-cols-3 gap-5">
            {ind.benefits.map((b: { title: string; text: string }) => (
              <div key={b.title} className="rounded-lg bg-card border border-border p-6">
                <div className="flex items-center gap-2 text-safety font-bold">
                  <CheckCircle2 className="h-5 w-5" /> {b.title}
                </div>
                <p className="mt-2 text-sm text-muted-foreground">{b.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How it works */}
      <section className="py-16">
        <div className="container-x max-w-4xl">
          <h2 className="font-display text-3xl uppercase text-center">
            How {st.name} {ind.short} Get Free Vending in 3 Steps
          </h2>
          <div className="mt-10 grid md:grid-cols-3 gap-5">
            {[
              { n: "1", t: "Apply Online", d: `Submit your ${st.name} ${ind.short.toLowerCase()} location details — 60-second form.` },
              { n: "2", t: "Customize Menu", d: `Choose the exact snacks, drinks, and healthy SKUs your ${st.name} team wants.` },
              { n: "3", t: "We Install & Service", d: `Local ${st.name} team delivers, installs, and services the machine — forever, at no cost.` },
            ].map((s) => (
              <div key={s.n} className="rounded-lg border border-border p-5 bg-card">
                <div className="font-display text-safety text-3xl">{s.n}</div>
                <h3 className="mt-1 font-display uppercase">{s.t}</h3>
                <p className="mt-1 text-sm text-muted-foreground">{s.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Top cities */}
      <section className="py-16 bg-secondary">
        <div className="container-x">
          <h2 className="font-display text-3xl uppercase text-center">
            {ind.short} Vending Coverage Across {st.name}
          </h2>
          <p className="mt-3 text-center text-muted-foreground">
            We serve {ind.name.toLowerCase()} in every {st.name} metro. Popular cities include:
          </p>
          <div className="mt-8 grid grid-cols-2 sm:grid-cols-3 gap-3 max-w-3xl mx-auto">
            {topCityLabels.map((city: string) => {
              const slug = city.toLowerCase().replace(/\s+/g, "-");
              return (
                <Link
                  key={city}
                  to="/states/$state/$town"
                  params={{ state: st.slug, town: slug }}
                  className="flex items-center gap-2 rounded-md bg-card border border-border px-4 py-3 text-sm hover:border-safety hover:text-safety transition"
                >
                  <MapPin className="h-4 w-4 text-safety shrink-0" />
                  <span className="font-medium">{seo?.placementPhrase ?? ind.short + " vending"} {city}</span>
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      {/* Local FAQs (AEO) */}
      <section className="py-16">
        <div className="container-x max-w-3xl">
          <h2 className="font-display text-3xl uppercase text-center">
            {ind.short} Vending in {st.name} — FAQs
          </h2>
          <div className="mt-10 space-y-4">
            {localFaqs.map((f) => (
              <details key={f.q} className="rounded-lg border border-border bg-card p-5 group">
                <summary className="font-semibold cursor-pointer flex justify-between gap-3">
                  {f.q}
                  <span className="text-safety group-open:rotate-45 transition">+</span>
                </summary>
                <p className="mt-3 text-sm text-muted-foreground leading-relaxed">{f.a}</p>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* Cross-links */}
      <section className="py-14 bg-secondary">
        <div className="container-x">
          <h2 className="font-display text-2xl uppercase text-center">Related Pages</h2>
          <div className="mt-6 grid sm:grid-cols-3 gap-4 max-w-4xl mx-auto">
            <Link to="/industries/$industry" params={{ industry: ind.slug }} className="rounded-lg bg-card border border-border p-5 hover:border-safety transition">
              <div className="font-display uppercase">{ind.name} — National</div>
              <div className="mt-1 text-sm text-muted-foreground">Full US program overview.</div>
            </Link>
            <Link to="/states/$state" params={{ state: st.slug }} className="rounded-lg bg-card border border-border p-5 hover:border-safety transition">
              <div className="font-display uppercase">All Industries in {st.name}</div>
              <div className="mt-1 text-sm text-muted-foreground">State-wide coverage & cities.</div>
            </Link>
            <Link to="/coverage" className="rounded-lg bg-card border border-border p-5 hover:border-safety transition">
              <div className="font-display uppercase">USA Coverage Map</div>
              <div className="mt-1 text-sm text-muted-foreground">All 50 states.</div>
            </Link>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-16 bg-charcoal text-charcoal-foreground">
        <div className="container-x max-w-3xl text-center">
          <h2 className="font-display text-3xl uppercase">
            Get a Free Vending Machine at Your {st.name} {ind.short}
          </h2>
          <p className="mt-4 text-white/85">
            48-hour approval. 1–2 week install. Zero cost. {st.name}-based service team.
          </p>
          <Link
            to="/"
            hash="contact"
            onClick={() => trackApplyClick(`industry-state:${ind.slug}:${st.slug}:footer`)}
            className="mt-6 inline-flex items-center gap-2 rounded-md bg-safety px-7 py-3.5 font-semibold text-safety-foreground shadow-[var(--shadow-cta)]"
          >
            Apply for Your Free Vending Machine <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
      </section>
    </div>
  );
}
